import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import NavigationMenu from '@/components/shared/Menu';
import { ThemeToggle } from '@/components/shared';
import { getApiUrl } from '@/config/api';
import { retryJsonFetch, RetryOptions } from '@/utils/retryHelper';
import { Sparkles } from '@/utils/icons';

/**
 * WhoopOAuthCallback component handles the OAuth callback from Whoop.
 * Processes the authorization code and exchanges it for access tokens.
 */
const WhoopOAuthCallback: React.FC = () => {
    // Add keyframes for the expand animation
    React.useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            @keyframes expand {
                0% {
                    transform: translateY(-20px);
                    opacity: 0;
                }
                100% {
                    transform: translateY(0);
                    opacity: 1;
                }
            }
        `;
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const [currentStep, setCurrentStep] = useState(0);
    const [processing, setProcessing] = useState(true);
    const [result, setResult] = useState<{ success: boolean; message?: string; error?: string; expires_in?: number; scopes?: string[] } | null>(null);
    const [userInfo, setUserInfo] = useState<{ username: string; role?: string; full_name?: string; first_name?: string } | null>(null);
    const processedRef = useRef(false);
    const navigate = useNavigate();
    const { isDark } = useTheme();
    const [searchParams] = useSearchParams();

    const steps = [
        'Verifying OAuth response',
        'Exchanging authorization code',
        'Storing secure tokens',
        'Testing connection'
    ];

    const handleContinue = () => {
        if (result?.success) {
            navigate('/dashboard');
        } else {
            navigate('/connect-whoop');
        }
    };

    useEffect(() => {
        // Prevent duplicate processing in React Strict Mode
        if (processedRef.current) {
            return;
        }
        processedRef.current = true;

        // Check if user is logged in
        const token = localStorage.getItem('access');
        if (!token) {
            navigate('/login');
            return;
        }

        // Get user info from localStorage
        const userInfoString = localStorage.getItem('user_info');
        if (userInfoString) {
            try {
                const user = JSON.parse(userInfoString);
                setUserInfo(user);
            } catch (err) {
                console.error('Error parsing user info:', err);
            }
        }

        const processOAuthCallback = async () => {
            try {
                // Extract parameters from URL
                const code = searchParams.get('code');
                const state = searchParams.get('state');
                const error = searchParams.get('error');
                const errorDescription = searchParams.get('error_description');

                // Step 1: Validate OAuth response
                setCurrentStep(0);
                await new Promise(resolve => setTimeout(resolve, 500)); // Brief delay for UX

                if (error) {
                    throw new Error(`OAuth error: ${error}. ${errorDescription || ''}`);
                }

                if (!code || !state) {
                    throw new Error('Missing required OAuth parameters (code or state)');
                }

                // Validate state parameter from localStorage
                const storedState = localStorage.getItem('whoop_oauth_state');
                if (!storedState || storedState !== state) {
                    throw new Error('Invalid state parameter - possible CSRF attack');
                }

                // Step 2: Exchange authorization code
                setCurrentStep(1);
                await new Promise(resolve => setTimeout(resolve, 500));

                const token = localStorage.getItem('access');
                if (!token) {
                    throw new Error('Authentication required. Please log in again.');
                }

                // Retry options optimized for OAuth callback processing
                const retryOptions: RetryOptions = {
                    maxAttempts: 5, // Critical OAuth callback - more retries
                    initialDelay: 1500,
                    maxDelay: 10000,
                    timeout: 30000, // 30 second timeout for token exchange
                    retryCondition: (error) => {
                        // Retry on network errors, timeouts, and server errors
                        // Don't retry on 4xx client errors (invalid code, expired state, etc.)
                        if (error.status >= 400 && error.status < 500 && error.status !== 429) {
                            // Log client errors for debugging but don't retry
                            console.error('OAuth callback client error (not retrying):', error);
                            return false;
                        }
                        return error.status >= 500 || 
                               error.status === 429 ||
                               error.message.includes('fetch') ||
                               error.message.includes('timeout') ||
                               error.name === 'AbortError';
                    }
                };

                const result = await retryJsonFetch(
                    getApiUrl('/api/whoop/callback/'),
                    {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Content-Type': 'application/json',
                        },
                        credentials: 'include',
                        body: JSON.stringify({
                            code: code,
                            state: state
                        }),
                    },
                    retryOptions
                );

                if (!result.success || !result.data) {
                    // Provide detailed error message based on retry results
                    let errorMessage = result.error || 'Failed to exchange authorization code';
                    
                    if (result.attempts > 1) {
                        errorMessage = `OAuth callback failed after ${result.attempts} attempts. ${errorMessage}`;
                    }
                    
                    // Add context for common AWS issues
                    if (result.error?.includes('timeout')) {
                        errorMessage += ' The server took too long to process the OAuth callback. This may be due to high load.';
                    } else if (result.error?.includes('fetch')) {
                        errorMessage += ' Network connection was interrupted during OAuth processing.';
                    }
                    
                    throw new Error(errorMessage);
                }

                const data = result.data;
                
                // Log successful callback processing
                console.log(`OAuth callback processed successfully after ${result.attempts} attempts (${result.totalTime}ms)`);
                
                // Add a small delay to prevent race conditions with token storage
                await new Promise(resolve => setTimeout(resolve, 200));

                // Step 3: Storing access tokens
                setCurrentStep(2);
                await new Promise(resolve => setTimeout(resolve, 500));

                // Clean up state from localStorage after successful exchange
                localStorage.removeItem('whoop_oauth_state');

                // Clean up any temporary credentials (legacy)
                sessionStorage.removeItem('whoop_oauth_credentials');

                // Refresh user info from backend to update whoop_connected status
                // Note: token is already available from line 114
                if (token) {
                    try {
                        const userInfoResponse = await fetch(getApiUrl('/api/users/me/'), {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                                'Content-Type': 'application/json',
                            },
                        });
                        
                        if (userInfoResponse.ok) {
                            const freshUserInfo = await userInfoResponse.json();
                            localStorage.setItem('user_info', JSON.stringify(freshUserInfo));
                            console.log('Updated user_info after Whoop connection');
                        }
                    } catch (err) {
                        console.error('Failed to refresh user info:', err);
                        // Non-critical error, continue with flow
                    }
                }

                // Step 4: Connection complete
                setCurrentStep(3);
                await new Promise(resolve => setTimeout(resolve, 500));

                setResult({
                    success: true,
                    message: data.message,
                    expires_in: data.expires_in,
                    scopes: data.scopes
                });

            } catch (error) {
                console.error('OAuth callback error:', error);
                // Clean up state on error
                localStorage.removeItem('whoop_oauth_state');
                setResult({
                    success: false,
                    error: error instanceof Error ? error.message : 'Unknown error occurred'
                });
            } finally {
                setProcessing(false);
            }
        };

        processOAuthCallback();
    }, [searchParams, navigate]);

    return (
        <div className="cgm-bg-primary min-h-screen flex flex-col items-center justify-center py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300">

            {/* Navigation Menu & Theme Toggle - Fixed Position */}
            <div className="fixed top-4 left-4 z-50">
                <NavigationMenu
                    userInfo={userInfo}
                    isDark={isDark}
                    showNavigation={true}
                />
            </div>

            <div className="fixed top-4 right-4 z-50">
                <ThemeToggle />
            </div>

            <div className="max-w-2xl w-full space-y-6">
                <div className="flex flex-col items-center">
                    <img
                        src={isDark ? "/logos/whoop/Logo/Wordmark/WHOOP Logo White.svg" : "/logos/whoop/Logo/Wordmark/WHOOP Logo Black.svg"}
                        alt="WHOOP Logo"
                        style={{ minWidth: 100, maxWidth: 200 }}
                    />
                </div>
                {/* Header */}
                <div className="text-center">
                    <h2 className="cgm-text-primary text-3xl font-light tracking-tight mb-2">
                        Whoop OAuth Connection
                    </h2>
                    <p className="cgm-text-secondary text-sm">
                        Processing your Whoop account connection...
                    </p>
                </div>

                {/* Progress Steps */}
                <div className="cgm-card p-8 transition-all duration-500 ease-in-out transform origin-top">
                    <div className="space-y-4 transition-all duration-500 ease-in-out">
                        {steps.map((label, index) => (
                            <div key={label} className="flex items-center transition-all duration-500 ease-in-out">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-4 transition-all duration-500 ease-in-out ${index < currentStep || result?.success ? 'bg-green-500' :
                                        index === currentStep && !result?.error ? 'bg-blue-500' :
                                            index === currentStep && result?.error ? 'bg-red-500' :
                                                'bg-gray-300 dark:bg-gray-600'
                                    }`}>
                                    {
                                        (index < currentStep || result?.success) ? (
                                            <svg className="w-5 h-5 text-white transition-all duration-500 ease-in-out" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        ) :
                                            (index === currentStep && result?.error) ? (
                                                <svg className="w-5 h-5 text-white transition-all duration-500 ease-in-out" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            ) :
                                                (<span className="text-white">{index + 1}</span>)
                                    }
                                </div>
                                <span className="cgm-text-primary">{label}</span>
                            </div>
                        ))}
                    </div>

                    {/* Loading State */}
                    {processing && (
                        <div className="flex items-center justify-center py-8 transition-all duration-500 ease-in-out">
                            <div className="animate-spin rounded-full h-8 w-8 cgm-spinner border-2 mr-3"></div>
                            <span className="cgm-text-primary">{steps[currentStep]}...</span>
                        </div>
                    )}

                    {/* Results */}
                    {!processing && result && (
                        <div className="mt-8 transition-all duration-500 ease-in-out transform origin-top">
                            {result.success ? (
                                <>
                                    <div className="cgm-alert-success rounded-lg p-4 mb-6 transition-all duration-700 ease-in-out transform opacity-0 animate-[expand_0.7s_ease-in-out_forwards]">
                                        <h3 className="text-xl font-semibold mb-2 flex items-center gap-2">
                                            <Sparkles className="w-5 h-5" /> Connection Successful!
                                        </h3>
                                        <p>
                                            {result.message || 'Your Whoop account has been connected successfully.'}
                                        </p>
                                    </div>

                                    <div className="mt-8 flex justify-center transition-all duration-700 ease-in-out transform opacity-0 animate-[expand_0.7s_ease-in-out_0.2s_forwards]">
                                        <button
                                            onClick={handleContinue}
                                            className="cgm-button px-6 py-3 flex items-center"
                                        >
                                            Go to Dashboard
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="cgm-alert-error rounded-lg p-4 mb-6 transition-all duration-700 ease-in-out transform opacity-0 animate-[expand_0.7s_ease-in-out_forwards]">
                                        <h3 className="text-xl font-semibold mb-2">
                                            Connection Failed
                                        </h3>
                                        <p>
                                            {result.error || 'An unknown error occurred during the connection process.'}
                                        </p>
                                    </div>

                                    <div className="mt-8 flex justify-center space-x-4 transition-all duration-700 ease-in-out transform opacity-0 animate-[expand_0.7s_ease-in-out_0.2s_forwards]">
                                        <button
                                            onClick={() => navigate('/connect-whoop')}
                                            className="cgm-button px-6 py-3"
                                        >
                                            Try Again
                                        </button>
                                        <button
                                            onClick={() => navigate('/dashboard')}
                                            className="cgm-button px-6 py-3"
                                        >
                                            Go to Dashboard
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>

                {/* Security Notice */}
                <div className="cgm-card p-4 transition-all duration-500 ease-in-out">
                    <p className="text-sm cgm-text-secondary">
                        <span className="font-medium">Security:</span> Your Whoop creds are never stored on our servers.
                        We only store secure OAuth tokens that can be revoked at any time through your
                        Whoop account settings.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default WhoopOAuthCallback; 